/**
 * Shared Products modal setup — used by deal cards and opportunity products section.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { notification } from 'antd';
import { CircleNotch } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../store/oneButtonStore';
import { CommerceHubModals } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/CommerceHubModals';
import { DashboardAuxiliarySection } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/sections/DashboardAuxiliarySection';
import {
    ProductsFinanceHubProvider,
    useOptionalProductsFinanceHubContext,
    useProductsFinanceHubContext,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/ProductsFinanceHubContext';
import type { HubGridRow } from '../../@types/oneButtonType';
import { ensurePreContractProductsHubReady } from '../lib/preContractProductsHubBootstrap';
import {
    preContractHubProductName,
    selectedHubProductNames,
} from '../lib/preContractProductCombinations';
import {
    financeMethodsEqual,
    partnerKeysEqual,
    resolveHubPartnerLabel,
    type PreContractDesignPartnerRecommendation,
} from '../lib/preContractProductPartners';
import type { PreContractProduct } from '../lib/preContractTypes';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/CommerceHubPanel.css';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/FinancePartnerSyncModal.css';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ProductsFinanceHubSection.css';

function isPartnerUnset(partner: string | null | undefined): boolean {
    const text = String(partner ?? '').trim().toLowerCase();
    return !text || text === 'select finance partner';
}

function isRowPartnerReady(row: HubGridRow | undefined): boolean {
    if (!row || row.methodsLoading) return false;
    const partner = String(row.finance_Partner_Display_Name ?? '').trim();
    if (!partner || isPartnerUnset(partner)) return false;
    if (partner.toLowerCase().includes('cash')) return true;
    return Boolean(String(row.finance_Method_Name ?? '').trim());
}

function resolveMethodOption(preferred: string, options: string[]): string | null {
    const trimmed = preferred.trim();
    if (!trimmed || options.length === 0) return null;
    if (options.includes(trimmed)) return trimmed;

    const lower = trimmed.toLowerCase();
    const caseInsensitive = options.find((option) => option.toLowerCase() === lower);
    if (caseInsensitive) return caseInsensitive;

    const preferredInOption = options.find((option) => option.toLowerCase().includes(lower));
    if (preferredInOption) return preferredInOption;

    const optionInPreferred = options.find((option) => lower.includes(option.toLowerCase()));
    if (optionInPreferred) return optionInPreferred;

    return null;
}

function rowMatchesDesignPartner(
    row: HubGridRow | undefined,
    recommendation: PreContractDesignPartnerRecommendation,
): boolean {
    if (!isRowPartnerReady(row)) return false;
    if (!partnerKeysEqual(recommendation.partner, row!.finance_Partner_Display_Name)) {
        return false;
    }
    const preferredMethod = recommendation.financeMethod?.trim();
    if (!preferredMethod) return true;
    return financeMethodsEqual(preferredMethod, row!.finance_Method_Name);
}

function waitForDesignPartnerApplied(
    readRow: () => HubGridRow | undefined,
    recommendation: PreContractDesignPartnerRecommendation,
    timeoutMs = 20000,
): Promise<HubGridRow> {
    return new Promise((resolve, reject) => {
        const started = Date.now();
        const tick = () => {
            const row = readRow();
            if (rowMatchesDesignPartner(row, recommendation)) {
                resolve(row!);
                return;
            }
            if (Date.now() - started >= timeoutMs) {
                reject(new Error('Partner sync timed out while applying design partner settings.'));
                return;
            }
            window.setTimeout(tick, 100);
        };
        tick();
    });
}

function waitForHubRowMethodsLoaded(
    readRow: () => HubGridRow | undefined,
    expectedPartner: string,
    timeoutMs = 20000,
): Promise<HubGridRow> {
    return new Promise((resolve, reject) => {
        const started = Date.now();
        const tick = () => {
            const row = readRow();
            const partner = String(row?.finance_Partner_Display_Name ?? '').trim();
            const methodsReady =
                Boolean(row?.finance_Method_Name?.trim()) ||
                Boolean(row?.methodOptions?.length) ||
                partner.toLowerCase().includes('cash');

            if (
                row &&
                !row.methodsLoading &&
                partner &&
                !isPartnerUnset(partner) &&
                partnerKeysEqual(expectedPartner, partner) &&
                methodsReady
            ) {
                resolve(row);
                return;
            }
            if (Date.now() - started >= timeoutMs) {
                reject(new Error('Finance methods timed out while loading.'));
                return;
            }
            window.setTimeout(tick, 100);
        };
        tick();
    });
}

export type PreContractProductSetupSummary = {
    product: PreContractProduct;
    hubName: string;
    partner: string | null;
    method: string | null;
    active: boolean;
};

export type PreContractPartnerSyncResult = {
    ok: boolean;
    message?: string;
};

type ProductsSetupContextValue = {
    openProductsSetup: (product?: PreContractProduct) => void;
    syncDesignPartner: (
        product: PreContractProduct,
        recommendation: PreContractDesignPartnerRecommendation,
    ) => Promise<PreContractPartnerSyncResult>;
    syncingProduct: PreContractProduct | null;
    blocked: boolean;
    /** oneDRAW design or losses changed — products / finance form is locked until finalize. */
    productsEditLocked: boolean;
    productsEditLockedReason: string | null;
    loading: boolean;
    statusError: string | null;
    productSummaries: PreContractProductSetupSummary[];
    hasUnsavedChanges: boolean;
    allPartnersSet: boolean;
    suggestedSolarPartner: string | null;
    selectedProducts: PreContractProduct[];
};

const ProductsSetupContext = createContext<ProductsSetupContextValue | null>(null);

export function usePreContractProductsSetup(): ProductsSetupContextValue {
    const ctx = useContext(ProductsSetupContext);
    if (!ctx) {
        throw new Error('usePreContractProductsSetup must be used within PreContractProductsSetupProvider');
    }
    return ctx;
}

export function useOptionalPreContractProductsSetup(): ProductsSetupContextValue | null {
    return useContext(ProductsSetupContext);
}

type ProviderProps = {
    salesForceId: string;
    selectedProducts: PreContractProduct[];
    suggestedSolarPartner?: string | null;
    opportunityBlockReason?: string | null;
    designPendingBlockReason?: string | null;
    children: ReactNode;
};

const ProductsSetupContextBridge: React.FC<Omit<ProviderProps, 'children'> & { children: ReactNode }> = ({
    salesForceId,
    selectedProducts,
    suggestedSolarPartner = null,
    opportunityBlockReason = null,
    designPendingBlockReason = null,
    children,
}) => {
    const hub = useOptionalProductsFinanceHubContext();
    if (!hub) {
        return (
            <ProductsFinanceHubProvider salesForceId={salesForceId}>
                <ProductsSetupContextBridge
                    salesForceId={salesForceId}
                    selectedProducts={selectedProducts}
                    suggestedSolarPartner={suggestedSolarPartner}
                    opportunityBlockReason={opportunityBlockReason}
                    designPendingBlockReason={designPendingBlockReason}
                >
                    {children}
                </ProductsSetupContextBridge>
            </ProductsFinanceHubProvider>
        );
    }

    return (
        <ProductsSetupContextBridgeInner
            salesForceId={salesForceId}
            selectedProducts={selectedProducts}
            suggestedSolarPartner={suggestedSolarPartner}
            opportunityBlockReason={opportunityBlockReason}
            designPendingBlockReason={designPendingBlockReason}
        >
            {children}
        </ProductsSetupContextBridgeInner>
    );
};

const ProductsSetupContextBridgeInner: React.FC<Omit<ProviderProps, 'children'> & { children: ReactNode }> = ({
    salesForceId,
    selectedProducts,
    suggestedSolarPartner = null,
    opportunityBlockReason = null,
    designPendingBlockReason = null,
    children,
}) => {
    const setProductsOpen = useOneButtonStore((s) => s.setCommerceHubProductsOpen);
    const financePartnerSyncModalOpen = useOneButtonStore(
        (s) => s.financePartnerSyncModalOpen,
    );
    const setFinancePartnerSyncModalOpen = useOneButtonStore(
        (s) => s.setFinancePartnerSyncModalOpen,
    );
    const leadSyncFormOpen = useOneButtonStore((s) => s.leadSyncFormOpen);
    const setLeadSyncFormOpen = useOneButtonStore((s) => s.setLeadSyncFormOpen);
    const opportunityId = useOneButtonStore((s) => s.opportunityID);
    const [syncingProduct, setSyncingProduct] = useState<PreContractProduct | null>(null);

    const {
        getRow,
        handleProductChange,
        setPartnerForProduct,
        setMethodForProduct,
        handleSaveProduct,
        rows,
        hasUnsavedChanges,
        hasProductUnsavedChanges,
        getProductValidationError,
        partnerOptionsForProduct,
        isOpportunityInitializing,
        opportunityLoadError,
    } = useProductsFinanceHubContext();

    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    const hubActionsRef = useRef({
        getRow,
        handleProductChange,
        setPartnerForProduct,
        setMethodForProduct,
        handleSaveProduct,
        getProductValidationError,
        hasProductUnsavedChanges,
        partnerOptionsForProduct,
    });
    hubActionsRef.current = {
        getRow,
        handleProductChange,
        setPartnerForProduct,
        setMethodForProduct,
        handleSaveProduct,
        getProductValidationError,
        hasProductUnsavedChanges,
        partnerOptionsForProduct,
    };

    const readHubRow = useCallback(
        (hubName: string) => rowsRef.current.find((row) => row.product_Type_Name === hubName),
        [],
    );

    const hubProductNames = useMemo(
        () => selectedHubProductNames(selectedProducts),
        [selectedProducts],
    );

    useEffect(() => {
        for (const productName of hubProductNames) {
            const row = getRow(productName);
            if (!row || row.activeFlag) continue;
            handleProductChange(productName, true);
        }
    }, [getRow, handleProductChange, hubProductNames, rows]);

    const productSummaries = useMemo(
        () =>
            selectedProducts.map((product, index) => {
                const hubName = hubProductNames[index] ?? product;
                const row = getRow(hubName);
                const partner = row?.finance_Partner_Display_Name?.trim() || null;
                const method = row?.finance_Method_Name?.trim() || null;
                return {
                    product,
                    hubName,
                    partner: isPartnerUnset(partner) ? null : partner,
                    method: method || null,
                    active: Boolean(row?.activeFlag),
                };
            }),
        [getRow, hubProductNames, rows, selectedProducts],
    );

    const allPartnersSet = productSummaries.every(
        (entry) => !entry.active || Boolean(entry.partner && entry.method),
    );

    const openProductsSetup = useCallback(
        (focusProduct?: PreContractProduct) => {
            if (opportunityBlockReason) return;
            if (designPendingBlockReason) {
                notification.warning({
                    message: 'Finalization pending',
                    description: designPendingBlockReason,
                    placement: 'topRight',
                });
                return;
            }

            const namesToActivate = focusProduct
                ? [preContractHubProductName(focusProduct)]
                : hubProductNames;

            for (const productName of namesToActivate) {
                const row = getRow(productName);
                if (!row?.activeFlag) {
                    handleProductChange(productName, true);
                }
            }

            if (
                selectedProducts.includes('Solar') &&
                suggestedSolarPartner &&
                (!focusProduct || focusProduct === 'Solar')
            ) {
                const solarRow = getRow('Solar');
                if (solarRow && isPartnerUnset(solarRow.finance_Partner_Display_Name)) {
                    setPartnerForProduct('Solar', suggestedSolarPartner, { activate: true });
                }
            }

            setProductsOpen(true);
        },
        [
            getRow,
            handleProductChange,
            hubProductNames,
            designPendingBlockReason,
            opportunityBlockReason,
            selectedProducts,
            setPartnerForProduct,
            setProductsOpen,
            suggestedSolarPartner,
        ],
    );

    const syncDesignPartner = useCallback(
        async (
            product: PreContractProduct,
            recommendation: PreContractDesignPartnerRecommendation,
        ): Promise<PreContractPartnerSyncResult> => {
            if (opportunityBlockReason) {
                return { ok: false, message: opportunityBlockReason };
            }
            if (designPendingBlockReason) {
                return { ok: false, message: designPendingBlockReason };
            }
            if (!recommendation.available || !recommendation.partner.trim()) {
                return {
                    ok: false,
                    message: recommendation.reason ?? 'Design partner is not available yet.',
                };
            }

            const hubName = preContractHubProductName(product);
            setSyncingProduct(product);

            try {
                const hub = hubActionsRef.current;
                const currentRow = hub.getRow(hubName);
                if (!currentRow?.activeFlag) {
                    hub.handleProductChange(hubName, true);
                }

                const hubPartner = resolveHubPartnerLabel(
                    recommendation.partner,
                    hub.partnerOptionsForProduct(hubName),
                );

                hub.setPartnerForProduct(hubName, hubPartner, { activate: true });

                const methodsLoadedRow = await waitForHubRowMethodsLoaded(
                    () => readHubRow(hubName),
                    hubPartner,
                );

                const preferredMethod = recommendation.financeMethod?.trim();
                const methodOptions = methodsLoadedRow.methodOptions ?? [];
                let resolvedMethodOption = preferredMethod
                    ? resolveMethodOption(preferredMethod, methodOptions)
                    : null;

                if (!resolvedMethodOption && preferredMethod && methodOptions.length > 0) {
                    resolvedMethodOption = methodOptions[0] ?? null;
                }

                if (
                    resolvedMethodOption &&
                    !financeMethodsEqual(resolvedMethodOption, methodsLoadedRow.finance_Method_Name)
                ) {
                    hub.setMethodForProduct(hubName, resolvedMethodOption);
                    await waitForHubRowMethodsLoaded(() => readHubRow(hubName), hubPartner);
                }

                const methodForWait =
                    resolvedMethodOption ??
                    readHubRow(hubName)?.finance_Method_Name ??
                    recommendation.financeMethod ??
                    '';

                const appliedRow = await waitForDesignPartnerApplied(
                    () => readHubRow(hubName),
                    {
                        ...recommendation,
                        partner: hubPartner,
                        financeMethod: methodForWait,
                    },
                );

                const savedMethod = appliedRow.finance_Method_Name?.trim() || '';
                const savedPartner = appliedRow.finance_Partner_Display_Name?.trim() || recommendation.partner;

                await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
                await hubActionsRef.current.handleSaveProduct(hubName);

                const validationMessage = hubActionsRef.current.getProductValidationError(hubName);
                if (
                    validationMessage ||
                    hubActionsRef.current.hasProductUnsavedChanges(hubName)
                ) {
                    throw new Error(
                        validationMessage ??
                            'Partner could not be saved. Open Configure & save to review.',
                    );
                }

                notification.success({
                    message: `${product} partner saved`,
                    description: `${savedPartner}${
                        savedMethod ? ` · ${savedMethod}` : ''
                    } saved to opportunity products.`,
                    placement: 'topRight',
                });

                return { ok: true };
            } catch (error) {
                const message =
                    error instanceof Error ? error.message : 'Could not save partner to opportunity products.';
                notification.error({
                    message: `${product} partner save failed`,
                    description: message,
                    placement: 'topRight',
                });
                return { ok: false, message };
            } finally {
                setSyncingProduct(null);
            }
        },
        [designPendingBlockReason, opportunityBlockReason, readHubRow],
    );

    const blocked = Boolean(opportunityBlockReason);
    const productsEditLocked = Boolean(designPendingBlockReason);
    const loading =
        Boolean(opportunityBlockReason?.includes('Loading')) || isOpportunityInitializing;
    const statusError =
        opportunityBlockReason && !opportunityBlockReason.includes('Loading')
            ? opportunityBlockReason
            : opportunityLoadError;

    const value = useMemo(
        (): ProductsSetupContextValue => ({
            openProductsSetup,
            syncDesignPartner,
            syncingProduct,
            blocked,
            productsEditLocked,
            productsEditLockedReason: designPendingBlockReason,
            loading,
            statusError,
            productSummaries,
            hasUnsavedChanges,
            allPartnersSet,
            suggestedSolarPartner,
            selectedProducts,
        }),
        [
            allPartnersSet,
            blocked,
            designPendingBlockReason,
            hasUnsavedChanges,
            loading,
            openProductsSetup,
            productSummaries,
            productsEditLocked,
            selectedProducts,
            statusError,
            suggestedSolarPartner,
            syncDesignPartner,
            syncingProduct,
        ],
    );

    return (
        <ProductsSetupContext.Provider value={value}>
            {children}
            <CommerceHubModals salesForceId={salesForceId} opportunityNumericId={opportunityId} />
            <DashboardAuxiliarySection
                financePartnerSyncModalOpen={financePartnerSyncModalOpen}
                setFinancePartnerSyncModalOpen={setFinancePartnerSyncModalOpen}
                leadSyncFormOpen={leadSyncFormOpen}
                setLeadSyncFormOpen={setLeadSyncFormOpen}
            />
        </ProductsSetupContext.Provider>
    );
};

export const PreContractProductsSetupProvider: React.FC<ProviderProps> = ({
    salesForceId,
    selectedProducts,
    suggestedSolarPartner = null,
    opportunityBlockReason = null,
    designPendingBlockReason = null,
    children,
}) => {
    const [bootstrapReady, setBootstrapReady] = useState(false);
    const [bootstrapError, setBootstrapError] = useState(false);

    useEffect(() => {
        let disposed = false;
        setBootstrapReady(false);
        setBootstrapError(false);

        ensurePreContractProductsHubReady(salesForceId)
            .then(() => {
                if (!disposed) setBootstrapReady(true);
            })
            .catch(() => {
                if (!disposed) setBootstrapError(true);
            });

        return () => {
            disposed = true;
        };
    }, [salesForceId]);

    if (bootstrapError) {
        return (
            <section className="pci-unified-deal pci-unified-products-setup" aria-label="Opportunity products">
                <p
                    className="pci-unified-products-setup__status pci-unified-products-setup__status--error"
                    role="alert"
                >
                    Opportunity products could not be loaded.
                </p>
            </section>
        );
    }

    if (!bootstrapReady) {
        return (
            <section className="pci-unified-deal pci-unified-products-setup" aria-label="Opportunity products">
                <header className="pci-unified-deal__head">
                    <h2 className="pci-unified-deal__title">Products</h2>
                    <p className="pci-unified-deal__subtitle">Loading opportunity products</p>
                </header>
                <div className="pci-product-cards pci-product-cards--loading" role="status" aria-live="polite">
                    <CircleNotch className="pci-ai-designs__spinner" size={22} weight="bold" aria-hidden />
                    <span>Loading products…</span>
                </div>
            </section>
        );
    }

    return (
        <ProductsFinanceHubProvider salesForceId={salesForceId}>
            <ProductsSetupContextBridge
                salesForceId={salesForceId}
                selectedProducts={selectedProducts}
                suggestedSolarPartner={suggestedSolarPartner}
                opportunityBlockReason={opportunityBlockReason}
                designPendingBlockReason={designPendingBlockReason}
            >
                {children}
            </ProductsSetupContextBridge>
        </ProductsFinanceHubProvider>
    );
};
